import { type ChangeEvent, useRef } from "react";

/**
 * Debounces calls to `onSearch`, returning both raw `debounce`/`flush` helpers
 * and ready-made `onChange`/`onPressEnter` handlers for `<Input>`.
 */
export const useDebouncedSearch = (onSearch: (value: string) => void, delay = 300) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  /** Schedule a debounced call — resets the timer on each invocation. */
  const debounce = (value: string) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(value), delay);
  };

  /** Cancel any pending debounce and call immediately. */
  const flush = (value: string) => {
    clearTimeout(debounceRef.current);
    onSearch(value);
  };

  /** Input onChange handler — debounces the search. */
  const onChange = (e: ChangeEvent<HTMLInputElement>) => debounce(e.target.value);

  /** Input onPressEnter handler — fires immediately. */
  const onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => flush(e.currentTarget.value);

  return { debounce, flush, onChange, onPressEnter };
};
